import type { IEventVaultSettingsResponse } from "@/types"
import { SocialsBehavior, TaggingBehavior } from "@/types"
import { WishlistMode } from "@/types/wishlist.interface"

export enum ExperienceType {
  MultiPage = 0,
  SinglePage = 1,
}

export enum EventWebsiteUrlMode {
  Off = 0,
  PathBased = 1,
  CustomDomain = 2,
}

export function isEventWebsiteUrlModeEnabled(mode?: EventWebsiteUrlMode): boolean {
  return mode === EventWebsiteUrlMode.PathBased
}

export function isEventWebsiteUrlModeVisible(mode?: EventWebsiteUrlMode): boolean {
  return mode === EventWebsiteUrlMode.Off || mode === EventWebsiteUrlMode.PathBased
}

// GetEvent serialises WishlistMode as a plain integer (0=Off, 1=Curated, 2=Custom), while the
// dedicated wishlist endpoint serialises the same underlying value as a string. Map to the shared
// string enum so callers only ever deal with one WishlistMode type.
function mapWishlistMode(mode?: number): WishlistMode | undefined {
  switch (mode) {
    case 0:
      return WishlistMode.Off
    case 1:
      return WishlistMode.Curated
    case 2:
      return WishlistMode.Custom
    default:
      return undefined
  }
}

export class EventVaultSettings {
  public id?: number
  public eventVaultId?: number
  public sharePixWithGuests: boolean = false
  public eventGuestMax: number = 0
  public videoEnabled: boolean = false
  public albumsEnabled: boolean = false
  public allowVideoGuestbook: boolean = false
  public allowAlbumNavigation: boolean = false
  public allowRootDirectoryUpload: boolean = false
  public allowGuestDownload: boolean = false
  public allowHostDownload: boolean = false
  public allowEventTemplate: boolean = false
  public streamingAllowed: boolean = false
  public shareGuestbookWithGuests: boolean = false
  public guestEmailRequired: boolean = false
  public hideGuestEmail: boolean = false
  public hideGuestbook: boolean = false
  public disableGuestUpload: boolean = false
  public maxNoOfAlbums: number = 0
  public hideMainGallery: boolean = false
  public socialsBehavior?: SocialsBehavior
  public taggingBehavior?: TaggingBehavior
  public allowGuestZipDownload: boolean = false
  public invitationEnabled?: boolean
  public allowThemeEdit?: boolean
  public moderatePhotosAndVideos?: boolean
  public moderateMessagesAndComments?: boolean
  public experienceType?: ExperienceType
  public rsvpEmailEnabled?: boolean
  public eventWebsiteUrlMode?: EventWebsiteUrlMode
  public wishlistMode?: WishlistMode

  constructor({
    id,
    eventVaultId,
    sharePixWithGuests,
    eventGuestMax,
    videoEnabled,
    albumsEnabled,
    allowVideoGuestbook,
    allowAlbumNavigation,
    allowRootDirectoryUpload,
    allowGuestDownload,
    allowHostDownload,
    allowEventTemplate,
    streamingAllowed,
    shareGuestbookWithGuests,
    guestEmailRequired,
    hideGuestEmail,
    hideGuestbook,
    disableGuestUpload,
    maxNoOfAlbums,
    hideMainGallery,
    socialsBehavior,
    taggingBehavior,
    allowGuestZipDownload,
    invitationEnabled,
    allowThemeEdit,
    moderatePhotosAndVideos,
    moderateMessagesAndComments,
    experienceType,
    rsvpEmailEnabled,
    eventWebsiteUrlMode,
    wishlistMode,
  }: Partial<EventVaultSettings>) {
    this.id = id
    this.eventVaultId = eventVaultId
    this.sharePixWithGuests = sharePixWithGuests ?? false
    this.eventGuestMax = eventGuestMax ?? 0
    this.videoEnabled = videoEnabled ?? false
    this.albumsEnabled = albumsEnabled ?? false
    this.allowVideoGuestbook = allowVideoGuestbook ?? false
    this.allowAlbumNavigation = allowAlbumNavigation ?? false
    this.allowRootDirectoryUpload = allowRootDirectoryUpload ?? false
    this.allowGuestDownload = allowGuestDownload ?? false
    this.allowHostDownload = allowHostDownload ?? false
    this.allowEventTemplate = allowEventTemplate ?? false
    this.streamingAllowed = streamingAllowed ?? false
    this.shareGuestbookWithGuests = shareGuestbookWithGuests ?? false
    this.guestEmailRequired = guestEmailRequired ?? false
    this.hideGuestEmail = hideGuestEmail ?? false
    this.hideGuestbook = hideGuestbook ?? false
    this.disableGuestUpload = disableGuestUpload ?? false
    this.maxNoOfAlbums = maxNoOfAlbums ?? 0
    this.hideMainGallery = hideMainGallery ?? false
    this.socialsBehavior = socialsBehavior ?? SocialsBehavior.Disabled
    this.taggingBehavior = taggingBehavior ?? TaggingBehavior.Disabled
    this.allowGuestZipDownload = allowGuestZipDownload ?? false
    this.invitationEnabled = invitationEnabled
    this.allowThemeEdit = allowThemeEdit ?? false
    this.moderatePhotosAndVideos = moderatePhotosAndVideos
    this.moderateMessagesAndComments = moderateMessagesAndComments
    this.experienceType = experienceType
    this.rsvpEmailEnabled = rsvpEmailEnabled
    this.eventWebsiteUrlMode = eventWebsiteUrlMode
    this.wishlistMode = wishlistMode
  }

  public static fromResponse(response?: IEventVaultSettingsResponse): EventVaultSettings {
    return new EventVaultSettings({
      id: response?.EventVaultSettingsID,
      eventVaultId: response?.EventVaultID,
      sharePixWithGuests: response?.SharePixWithGuests,
      eventGuestMax: response?.EventGuestMax,
      videoEnabled: response?.VideoEnabled,
      albumsEnabled: response?.AlbumsEnabled,
      allowVideoGuestbook: response?.AllowVideoGuestbook,
      allowAlbumNavigation: response?.AllowAlbumNavigation,
      allowRootDirectoryUpload: response?.AllowRootDirectoryUpload,
      allowGuestDownload: response?.AllowGuestDownload,
      allowHostDownload: response?.AllowHostDownload,
      allowEventTemplate: response?.AllowEventTemplate,
      streamingAllowed: response?.StreamingAllowed,
      shareGuestbookWithGuests: response?.ShareGuestbookWithGuests,
      guestEmailRequired: response?.GuestEmailRequired,
      hideGuestEmail: response?.HideGuestEmail,
      hideGuestbook: response?.HideGuestbook,
      maxNoOfAlbums: response?.MaxNoOfAlbums,
      disableGuestUpload: response?.DisableGuestUpload,
      hideMainGallery: response?.HideMainGallery,
      socialsBehavior: response?.SocialsBehavior,
      taggingBehavior: response?.TaggingBehavior,
      allowGuestZipDownload: response?.AllowGuestZipDownload,
      invitationEnabled: response?.InvitationEnabled,
      allowThemeEdit: response?.AllowThemeEdit,
      moderatePhotosAndVideos: response?.ModeratePhotosAndVideos,
      moderateMessagesAndComments: response?.ModerateMessagesAndComments,
      experienceType: response?.ExperienceType,
      rsvpEmailEnabled: response?.RSVPEmailEnabled,
      eventWebsiteUrlMode: response?.EventWebsiteUrlMode,
      wishlistMode: mapWishlistMode(response?.WishlistMode),
    })
  }
}
